/**
 * Reusable modal dialog component.
 *
 * Replaces the legacy AngularJS ui-bootstrap $uibModal.
 * Renders a centered overlay with header/body/footer sections.
 * Closes on Escape key and optional backdrop click.
 *
 * The box is a `role="dialog"`, named by its title when it has one, and the
 * close button is named "Close" (jorigin/brewauth#29 GB1). While a centred
 * modal is open, the rest of the page is `inert` (`useInertBackground`), and
 * it sets no `aria-modal`: a people-and-groups picker inside it renders its
 * popup outside the box, which `aria-modal` would hide (GB8, see
 * `inertBackground.ts`). In side-dock mode the page behind stays usable, so
 * it is neither inert nor announced as modal. There is no focus trap yet.
 * On close, focus goes back to the element that had it when the modal
 * opened, even when something inside took focus with `autoFocus` (GB final).
 *
 * An Escape that something inside already handled (`defaultPrevented`, e.g.
 * a menu closing, an inline edit cancelling) closes only that thing; the
 * dialog stays, with its unsaved edits (#2356). A plain Escape closes it.
 */
import { useEffect, useCallback, useId, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { lockBodyScroll } from './bodyScrollLock';
import { useInertBackground } from './inertBackground';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  closeOnBackdrop?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** When > 0, render non-blocking and docked: the overlay ends `sideOffsetRight`
   *  px from the right edge (leaving room for the brew-ai panel) with no dimming
   *  backdrop, so both stay usable. 0/undefined = normal centered modal. */
  sideOffsetRight?: number;
  /** Extra props for the modal box — used by panel-owned modals to apply the
   *  panel appearance scope (CSS zoom + contrast attr). The max-width clamp
   *  divides by the zoom so the *visual* size honors the size cap and stays
   *  inside the viewport (zoom multiplies every rendered length). */
  surfaceProps?: { style: CSSProperties; 'data-pp-contrast'?: string };
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  // Data-heavy views (e.g. the audit grid): fill most of the viewport, capped
  // so it doesn't stretch unusably wide on ultrawide displays.
  '2xl': 'max-w-[min(94vw,1600px)]',
};

const sizeCaps = {
  sm: '24rem',
  md: '32rem',
  lg: '42rem',
  xl: '56rem',
  '2xl': 'min(94vw, 1600px)',
};

export default function Modal({
  open,
  onClose,
  title,
  size = 'md',
  closeOnBackdrop = true,
  children,
  footer,
  sideOffsetRight,
  surfaceProps,
}: ModalProps) {
  const titleId = useId();
  const boxRef = useRef<HTMLDivElement>(null);
  // Where focus goes back to on close: the element focused when `open`
  // turned true, recorded in THAT render, before the content mounts and an
  // `autoFocus` inside it (a Cancel, a Name field) moves focus in (GB final,
  // I3). Recorded during render as state derived from `open`, not in an
  // effect: React applies `autoFocus` before any effect runs.
  const [opener, setOpener] = useState<Element | null>(() =>
    open ? document.activeElement : null,
  );
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    setOpener(open ? document.activeElement : null);
  }
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  // Side-dock mode is non-blocking — keep the underlying page scrollable.
  // The lock is counted (`lockBodyScroll`): closing a dialog stacked over
  // another no longer unlocks the page under the one still open.
  useEffect(() => {
    if (!open || sideOffsetRight) return;
    return lockBodyScroll();
  }, [open, sideOffsetRight]);

  // Side-dock mode leaves the page usable; a centred modal makes it inert.
  useInertBackground(boxRef, open && !sideOffsetRight, opener);

  if (!open) return null;

  return createPortal(
    // Overlay onClick is a mouse-only convenience; keyboard users dismiss
    // via the document-level Escape handler above or the header close button.
    <div
      className="brew-modal-overlay"
      role="none"
      style={
        sideOffsetRight
          ? { left: 0, top: 0, bottom: 0, right: sideOffsetRight, background: 'transparent' }
          : undefined
      }
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div // NOSONAR typescript:S6847 accessibility-by-design: dialog surface; its listeners are mouse conveniences, keyboard dismiss is the document Escape and close button
        {...surfaceProps}
        ref={boxRef}
        role="dialog"
        aria-labelledby={title ? titleId : undefined}
        className={`brew-modal ${sizeClasses[size]} w-full`}
        style={
          surfaceProps
            ? {
                ...surfaceProps.style,
                maxWidth: `calc(min(${sizeCaps[size]}, 100vw - 32px) / ${
                  typeof surfaceProps.style.zoom === 'number' ? surfaceProps.style.zoom : 1
                })`,
              }
            : undefined
        }
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="brew-modal-header">
            <h3 id={titleId} className="text-lg font-bold">{title}</h3>
            <button type="button" onClick={onClose} className="brew-btn-icon" aria-label="Close">
              <FontAwesomeIcon icon={faXmark} className="text-lg" aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="brew-modal-body">{children}</div>
        {footer && <div className="brew-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
